// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import SolyticsBox from '../../../../package/components/SolyticsBox';
import PropTypes from 'prop-types';

import { getErrorHelperText } from '../../../../utils/Utils';
import { INPUT_LABEL_PROPS } from './TextFieldComponent';

/**
 * Text field extended component for editing text attributes
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - Attribute configuration object
 * @param {Object} props.entityDetailsFormikForm - Formik form instance
 * @param {string} props.titleLabel - Title label for the field
 * @returns {React.ReactElement} The TextFieldExtended component
 */
const TextFieldExtendedComponent = ({
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  label,
  titleLabel,
  isLoadingConstraints = false,
}) => {
  const MAX_LENGTH = 10000;

  const currentValue = useMemo(
    () => (value === undefined || value === null ? '' : String(value)),
    [value]
  );

  const characterCount = useMemo(() => currentValue.length, [currentValue]);

  /**
   * Handles input change events
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   * @returns {void}
   */
  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const errorHelperText = useMemo(
    () =>
      getErrorHelperText(
        entityDetailsFormikForm?.errors?.[attribute?.id],
        entityDetailsFormikForm?.touched?.[attribute?.id]
      ),
    [
      entityDetailsFormikForm?.errors,
      entityDetailsFormikForm?.touched,
      attribute?.id,
    ]
  );

  const helperTextContent = useMemo(() => {
    if (errorHelperText) {
      return (
        <SolyticsBox
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <span>{errorHelperText}</span>
          <SolyticsBox
            component="span"
            sx={{
              marginLeft: 'auto',
              color: 'text.secondary',
            }}
          >
            {characterCount}/{MAX_LENGTH}
          </SolyticsBox>
        </SolyticsBox>
      );
    }
    return (
      <SolyticsBox
        component="span"
        sx={{ display: 'flex', justifyContent: 'flex-end' }}
      >
        <SolyticsBox component="span" sx={{ color: 'text.secondary' }}>
          {characterCount}/{MAX_LENGTH}
        </SolyticsBox>
      </SolyticsBox>
    );
  }, [errorHelperText, characterCount]);

  return (
    <Grid item gap={2} container xs={12}>
      <Grid item xs={12}>
        <Typography
          variant="subtitle3"
          sx={{
            mb: 1,
          }}
        >
          {label}
        </Typography>
        <TextField
          {...entityDetailsFormikForm?.getFieldProps(attribute?.id)}
          title={titleLabel}
          fullWidth
          value={currentValue}
          placeholder="Enter a description"
          onChange={handleChange}
          multiline
          minRows={8}
          spellCheck
          error={Boolean(errorHelperText)}
          helperText={helperTextContent}
          disabled={!attribute?.is_editable || isLoadingConstraints}
          InputLabelProps={INPUT_LABEL_PROPS}
        />
      </Grid>
    </Grid>
  );
};

TextFieldExtendedComponent.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    is_editable: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func.isRequired,
    errors: PropTypes.objectOf(PropTypes.string).isRequired,
    touched: PropTypes.objectOf(PropTypes.bool).isRequired,
  }).isRequired,
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  isLoadingConstraints: PropTypes.bool,
};

export default TextFieldExtendedComponent;
